<template>
  <div class="verification-viewer">
    <!--
      Mount div MUST use id="viewer3d" — the shared `launchViewer` in
      scripts/js/forgeviewer.js selects its parent DOM node by that exact
      id (line 93). Using the same id keeps us on the single canonical
      3D-load path shared with Dashboard's ViewersSection / pageView.

      The modal v-overlay loader mirrors pageView.vue:140-149 — same
      "Loading…" title + striped progress bar the rest of the app uses
      for 3D viewer downloads. Status bar handles ambient state display
      after load; this overlay handles the opaque "can't use yet" phase.
    -->
    <div id="viewer3d" class="verification-viewer__mount">
      <v-overlay
        v-if="viewerLoading"
        value="true"
        opacity="0.85"
        absolute
        class="verification-viewer__overlay"
      >
        <v-sheet
          class="d-flex align-center justify-center flex-column"
          width="400"
          color="transparent"
        >
          <p class="text-h4 mb-4">Loading...</p>
          <v-progress-linear
            class="rounded-pill animate"
            striped
            stream
            height="30"
            :value="viewerLoadingPercent"
            color="primary"
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
        </v-sheet>
      </v-overlay>
    </div>

    <!-- Empty state (when the viewer hasn't started yet — e.g. cold deep-link
         before the facility context has resolved a master URN) -->
    <div v-if="showEmptyState" class="verification-viewer__empty">
      <svg
        class="verification-viewer__empty-icon"
        viewBox="0 0 48 48"
        width="40"
        height="40"
        aria-hidden="true"
      >
        <path
          d="M24 6 L42 16 L42 32 L24 42 L6 32 L6 16 Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <path
          d="M24 6 L24 24 M24 24 L42 16 M24 24 L6 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linejoin="round"
          opacity="0.65"
        />
      </svg>
      <div class="verification-viewer__empty-caption">3D Viewer</div>
      <div class="verification-viewer__empty-sub">Waiting for facility model…</div>
    </div>

    <div
      v-if="markerSettings.visible"
      class="verification-markers"
      :style="{ opacity: markerSettings.opacity, '--marker-scale': markerScale }"
      aria-hidden="true"
    >
      <span
        v-for="marker in projectedMarkers"
        :key="marker.id"
        class="verification-marker"
        :class="[
          `verification-marker--${marker.kind}`,
          { 'verification-marker--active': marker.active },
        ]"
        :style="{
          left: `${marker.x}px`,
          top: `${marker.y}px`,
          '--verification-marker-color': marker.color,
        }"
      >
        <span class="verification-marker__shape"></span>
        <span
          v-if="markerSettings.showLabels"
          class="verification-marker__label"
        >{{ marker.label }}</span>
        <!-- Deviation arrows. A child of the marker anchor on purpose: it then
             inherits the projected left/top, the `hiddenPointIds` filter, the
             all-vs-current scope, global marker visibility and the evidence
             capture of this whole layer, all by construction rather than by a
             second copy of each rule. -->
        <MarkerErrorVectors
          v-if="marker.vectors && marker.vectors.length"
          :vectors="marker.vectors"
          :scale="markerScale"
        />
      </span>
    </div>
  </div>
</template>

<script>
/**
 * Verification Workspace 3D viewer.
 *
 * Mounts the Forge 3D viewer on the verification route using the same
 * initializer path as Dashboard's ViewersSection — shared `id="viewer3d"`
 * mount contract, shared extension stack, shared Vuex viewerState loader
 * progress.
 *
 * Pre-flight setup (Phase 3):
 * Once BOTH the Forge model and the verified tags are ready, we:
 *   1. Isolate the verified tag's Forge dbIds via MainExtension.isolateDbIds.
 *   2. Apply a crop box = tag bbox + 20% margin via Cropping3DExtension,
 *      so design vs. as-built deviations stay in frame when comparing.
 *   3. Frame the camera on the cropped region.
 *   4. Overlay the best-matching point-cloud scan (combined preferred,
 *      individual fallback) via mainExtension.showPointCloud.
 *
 * This orchestration is watcher-driven: the Vuex getter GET_VERIFIED_BBOX
 * only returns non-null when `viewer.model.getInstanceTree()` is ready
 * AND `verifiedTags` has been fetched. So we watch that getter and apply
 * the full setup the moment it flips — no manual race management.
 *
 * URN source: dispatches `START_VIEWER_3D` (global Vuex action in
 * store/modules/viewers.js). Same code path ViewersSection, pageView,
 * and AddInspectToPlan all use.
 *
 * Lifecycle:
 * - mounted(): wait for #viewer3d to paint, then run startViewer().
 * - watcher on verifiedDbIds: fires the isolate+crop+scan orchestration
 *   once (and only once — `_preflightApplied` guards against re-entry).
 * - beforeDestroy(): full cleanup — endIsolation, clearCropBox, hide
 *   the scan WE enabled, reset Vuex, then the existing #forgeViewer
 *   DOM detach from the AVA-337 lifecycle fix. The Forge instance is
 *   NOT disposed; it stays cached for the next viewer page.
 */
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { EventBus } from '@/event-bus';
import { setModelVisibility, setModelTransparency } from '@/utils/forgeUtils';
import { computeViewPose } from '@/features/Verification/utils/computeViewPose';
import { listLoadedModels } from '@/features/Verification/utils/verifiedBBox';
import { assertViewerPoint } from '@/features/Verification/utils/modelUnits';
import {
  drawAxisTriad,
  removeAxisTriad,
  setAxisTriadOpacity,
} from '@/features/Verification/utils/axisTriad';
import { setScanVisibility } from '@/features/Verification/utils/scanVisibility';
import { markerColorForPointId } from '@/features/Verification/utils/markerPalette';
import { PLANE_3D } from '@/features/Verification/utils/asBuiltDraft';
import {
  buildVectorModel,
  computeAxisScreenVectors,
  epsilonForExtent,
} from '@/features/Verification/utils/errorVectors';
import MarkerErrorVectors from '@/features/Verification/components/workspace/MarkerErrorVectors.vue';

/**
 * Model-pick channel (AVA-475 Task 4).
 *
 * The consumer (`components/modals/DesignPointModal.vue`) is not an ancestor of
 * this component — it sits beside `WorkspacePanel`, not above it — so a plain
 * `@model-pick` binding is unreachable from there. The shared `EventBus`
 * (src/event-bus.js) is this codebase's established decoupling channel. We also
 * `$emit('model-pick', …)` so the declarative contract stays available to any
 * future parent that does wrap this component.
 *
 * Keep these names in step with the counterpart declaration in
 * `components/modals/DesignPointModal.vue`.
 */
const MODEL_PICK_EVENTS = Object.freeze({
  ARM: 'verification:arm-model-pick',
  CANCEL: 'verification:cancel-model-pick',
  RESULT: 'verification:model-pick',
  FAILED: 'verification:model-pick-failed',
});

/**
 * How far (px) the pointer may travel between press and release and still count
 * as a pick rather than a camera drag. Small enough that a deliberate click on a
 * feature always lands; large enough to tolerate hand tremor on a trackpad.
 */
const MODEL_PICK_DRAG_THRESHOLD_PX = 4;

/**
 * Pre-flight retry cadence.
 *
 * `GET_VERIFIED_BBOX` yields null until the FRAGMENTS behind those dbIds are
 * resolvable, which can lag GEOMETRY_LOADED_EVENT — measured live with
 * `modelReady: true`, `instanceTree` ready and `dbIdCount: 1`, yet `bbox === null`.
 *
 * Because the loaded models are external Forge objects Vue cannot observe, no
 * watcher re-fires when the fragments settle, so a single miss left the
 * workspace permanently un-isolated, un-cropped and scan-less. Polling is the
 * only signal available. 20 x 250 ms = 5 s, then we give up and surface it
 * rather than spinning forever.
 *
 * NOTE: the original cause of that permanent miss was NOT timing — it was
 * `getBBBox()` being structurally unable to reach the geometry (non-recursive
 * fragment walk). That is fixed in `utils/verifiedBBox.js`; this retry now
 * covers only the genuine load race it was meant to cover.
 */
const PREFLIGHT_RETRY_MS = 250;
const PREFLIGHT_MAX_ATTEMPTS = 20;

export default {
  name: 'VerificationViewer',
  // First component this file has ever mounted, hence the first `components`
  // option. It is NOT optional: Vue 2 renders an unregistered custom element
  // as an unknown HTML tag without failing the build, so a missing entry here
  // would show up as arrows that silently never appear.
  components: { MarkerErrorVectors },
  data() {
    return {
      projectedMarkers: [],
    };
  },
  // Non-reactive instance refs (Vue lint rejects `_`-prefixed keys in data()
  // since they clash with internal props — these stay plain `this.` assigns).
  // `_appliedScanName`: scan we enabled, so beforeDestroy can turn JUST that
  //   one back off without touching scans the user had on elsewhere.
  // `_isUnmounted`: set in beforeDestroy — guards async .then callbacks.
  // `_onGeometryLoadedBound`: bound event handler, kept for removeEventListener.
  // `_onModelPickClickBound` / `_onModelPickDownBound` / `_modelPickTarget` /
  //   `_modelPickDownAt` / `_modelPickPrevCursor`: armed-model-pick bookkeeping,
  //   so `disarmModelPick` can always detach the exact listeners it attached and
  //   put the cursor back. `_modelPickDownAt` is the press position used to tell
  //   a genuine click from a camera drag.
  // `_loggedVectorSignature`: last error-vector state written to the log, so a
  //   method that runs on every camera frame logs only when something changed.
  //
  // Preflight readiness flags (modelReady, preflightApplied, viewerError) live
  // in the VERIFICATION Vuex module now, not component data — so the sibling
  // ViewerStatusBar can observe them without prop-drilling or $refs.
  computed: {
    currentUrn() {
      return this.$store.state.currentUrn || null;
    },
    // Mirror pageView.vue's computed shape so we're on the same contract.
    // The `!isLoadDone()` refinement hides the loader as soon as the model
    // finishes, even if viewerState.isLoading hasn't flipped to false yet.
    // Loader stays visible through the full preflight pipeline, not just
    // the Forge download. Reuses the VERIFICATION/GET_VIEWER_STATUS cascade
    // so this stays in lockstep with the status bar's state machine:
    //   loading_model  → Forge is downloading / indexing the model
    //   preparing      → Geometry bytes in, waiting for GEOMETRY_LOADED_EVENT
    //   loading_tags   → Verified-tag fetch still in flight
    //   isolating      → Tags loaded, preflight (isolate + crop + scan) pending
    // Once cascade hits 'ready' (preflight applied) or 'error' / 'idle'
    // (no model to load), the overlay retires. Edge case handled: if
    // verifiedTags returns empty, the cascade skips 'isolating' entirely
    // and lands on 'ready' — overlay doesn't hang forever.
    viewerLoading() {
      const status = this.$store.getters['VERIFICATION/GET_VIEWER_STATUS'];
      return status === 'loading_model'
        || status === 'preparing'
        || status === 'loading_tags'
        || status === 'isolating';
    },
    // 0..100 download progress driven by launchViewer's progressCallback —
    // feeds the v-progress-linear inside the overlay so the user sees
    // actual percent rather than an indeterminate spinner.
    viewerLoadingPercent() {
      return this.$store.state.viewerState?.percent ?? 0;
    },
    // Cached #forgeViewer DOM node. Populated by launchViewer on first load
    // and preserved across route changes via SET_PAGE_VIEW_3D so we can
    // re-attach it without re-downloading the model. Mirrors pageView.vue.
    pageView3d() {
      return this.$store.getters.GET_PAGE_VIEW_3D;
    },
    // Empty state shows when: we never successfully started the viewer AND
    // we're not currently loading. Covers both "no model yet" and
    // "dispatch failed / asset has no master URN" cases.
    showEmptyState() {
      return !this.currentUrn && !this.viewerLoading;
    },
    // Preflight readiness flags — read-through from Vuex so this component
    // stays the sole *writer* via mutations while the rest of the UI
    // (status bar, potential future chrome) reads the same source of truth.
    modelReady() {
      return this.$store.state.VERIFICATION.modelReady;
    },
    preflightApplied() {
      return this.$store.state.VERIFICATION.preflightApplied;
    },
    // Vuex-reactive gate: flips to a non-empty array once LOAD_VERIFIED_TAGS
    // resolves and populates state.verifiedTags → GET_VERIFIED_DBIDS computes.
    // This is one of two signals that drive preflight (the other is the
    // GEOMETRY_LOADED_EVENT from the Forge viewer, which sets `modelReady`).
    // We can't watch the bbox getter directly because viewer.model is an
    // external Forge object — Vue can't observe its instanceTree becoming
    // non-null. The event listener is the only reliable signal for that.
    verifiedDbIds() {
      return this.$store.getters['VERIFICATION/GET_VERIFIED_DBIDS'];
    },
    /**
     * Scan chosen for the verified tag. Null until the facility scan catalogue
     * lands in root state (VERIFICATION/ENSURE_SCAN_CATALOGUE), so this
     * legitimately flips null → scan AFTER the pre-flight has already run.
     */
    selectedScan() {
      return this.$store.getters['VERIFICATION/GET_SELECTED_SCAN'];
    },
    markerSettings() {
      return this.$store.getters['VERIFICATION/GET_MARKER_SETTINGS'];
    },
    /**
     * Feeds `--marker-scale` on the marker layer, which every marker dimension
     * is a calc() of. Pure CSS, so a drag re-renders nothing but the style
     * attribute — no re-projection, no DOM churn. Carries a numeric fallback for
     * the same reason `axesSize` does: an undefined multiplier would resolve
     * every calc() to invalid and the markers would vanish.
     */
    markerScale() {
      return this.markerSettings?.markerSize ?? 1;
    },
    /**
     * How big a viewer unit is, from the store. THE ONLY SOURCE — this
     * component threads it into the two utils that carry metre constants
     * (`epsilonForExtent`, `drawAxisTriad`) so neither has to resolve units
     * itself. See utils/modelUnits.js for why that matters on this route.
     */
    unitTransform() {
      return this.$store.getters['VERIFICATION/GET_UNIT_TRANSFORM'];
    },
    /** Viewer units in one real metre, or null while the transform is pending. */
    viewerUnitsPerMetre() {
      const scale = this.unitTransform?.siteScale;
      return Number.isFinite(scale) && scale > 0 ? 1 / scale : null;
    },
    /**
     * World step used to probe each axis' screen direction for the deviation
     * arrows, sized to the verified geometry (see `epsilonForExtent`).
     *
     * Reads the bbox through the getter rather than caching it: the bbox is a
     * THREE.Box3 Vue cannot observe, but it is reached via `verifiedGeometry`
     * in STATE, which is committed — so this recomputes exactly when preflight
     * resolves the geometry and not on every frame. Null extent before that
     * falls back to the default step, so arrows drawn early still point
     * somewhere sensible.
     */
    vectorEpsilon() {
      const bbox = this.$store.getters['VERIFICATION/GET_VERIFIED_BBOX'];
      if (!bbox?.min || !bbox?.max) return epsilonForExtent(null, this.viewerUnitsPerMetre);
      return epsilonForExtent(Math.max(
        bbox.max.x - bbox.min.x,
        bbox.max.y - bbox.min.y,
        bbox.max.z - bbox.min.z,
      ), this.viewerUnitsPerMetre);
    },
    visualizationSettings() {
      return this.$store.getters['VERIFICATION/GET_VISUALIZATION_SETTINGS'];
    },
    /**
     * Axis-triad visibility, registered in the model/3D settings group.
     *
     * Read straight through with no local default: until the setting exists in
     * the registry this is `undefined` and the triad stays off, which is the
     * honest reading of "the store does not know about this setting yet". A
     * component-local fallback would diverge from the popover checkbox and hide
     * a registration failure behind a triad that appears but cannot be toggled.
     */
    showAxes() {
      return !!this.visualizationSettings?.model?.showAxes;
    },
    /**
     * Triad appearance. These DO carry numeric fallbacks, unlike `showAxes`
     * above: an undefined boolean reads as "off", which is a safe and visible
     * state, but an undefined multiplier would propagate NaN into the geometry
     * maths and the triad would vanish with no clue why.
     */
    axesOpacity() {
      return this.visualizationSettings?.model?.axesOpacity ?? 1;
    },
    axesSize() {
      return this.visualizationSettings?.model?.axesSize ?? 1;
    },
    markerModels() {
      if (!this.markerSettings?.visible) return [];
      const activePointId = this.$store.state.VERIFICATION.activePointId;
      const hidden = new Set(this.markerSettings.hiddenPointIds || []);
      const points = this.$store.getters['VERIFICATION/GET_POINTS_WITH_RESULTS'] || [];
      const preview = this.$store.getters['VERIFICATION/GET_DESIGN_PICK_PREVIEW'];
      const markers = [];

      points.forEach((point) => {
        if (hidden.has(point.id)) return;
        if (this.markerSettings.scope === 'current' && point.id !== activePointId) return;
        const color = markerColorForPointId(point.id);
        const active = point.id === activePointId;
        const hasPreview = preview?.pointId === point.id && preview?.point;

        if (this.markerSettings.showDesign) {
          const design = hasPreview ? preview.point : point.designAdjusted;
          if (this.isWorldPoint(design)) {
            markers.push({
              id: `${point.id}-design${hasPreview ? '-preview' : ''}`,
              point: design,
              pointId: point.id,
              kind: hasPreview ? 'preview' : 'design',
              label: `${point.label} · ${hasPreview ? 'DESIGN PREVIEW' : 'DESIGN'}`,
              color,
              active,
            });
          }
        }

        if (this.markerSettings.showAsBuilt && this.isWorldPoint(point.asBuiltAdjusted)) {
          markers.push({
            id: `${point.id}-as-built`,
            point: point.asBuiltAdjusted,
            pointId: point.id,
            kind: 'as-built',
            label: `${point.label} · AS-BUILT`,
            color,
            active,
            // Deviation arrows hang off the AS-BUILT marker only: it is the
            // measured end of the arrow, so the deviation reads as "this is
            // where it ended up, and by how much". Design and slice markers
            // get none — a draft pick has no verdict to show yet. Empty for an
            // incomplete point, which the template treats as "no arrows".
            vectors: buildVectorModel(point),
          });
        }
      });

      // Draft contributions retain their real 3D hits. The merged draft axes
      // are intentionally not rendered as a point because they may come from
      // two different clicks and would invent a location nobody selected.
      const draft = this.$store.getters['VERIFICATION/GET_AS_BUILT_DRAFT'];
      if (this.markerSettings.showAsBuilt
        && draft?.pointId === activePointId
        && !hidden.has(activePointId)) {
        const active = points.find((point) => point.id === activePointId);
        const color = markerColorForPointId(activePointId);
        Object.values(draft.contributions || {}).forEach((contribution) => {
          if (!this.isWorldPoint(contribution.point)) return;
          markers.push({
            id: `${activePointId}-slice-${contribution.plane}`,
            point: contribution.point,
            pointId: activePointId,
            kind: 'slice',
            label: `${active?.label || 'Point'} · ${contribution.plane} PICK`,
            color,
            active: true,
          });
        });
        const pending = draft.pendingConflict?.contribution;
        if (pending?.plane && this.isWorldPoint(pending.point)) {
          markers.push({
            id: `${activePointId}-slice-${pending.plane}-pending`,
            point: pending.point,
            pointId: activePointId,
            kind: 'conflict',
            label: `${active?.label || 'Point'} · ${pending.plane} PENDING`,
            color,
            active: true,
          });
        }
      }
      return markers;
    },
  },
  watch: {
    // Path 1 of 2: when tags arrive after the model is already loaded,
    // verifiedDbIds flips from [] to non-empty → try preflight.
    verifiedDbIds: {
      immediate: true,
      handler(dbIds) {
        if (dbIds?.length) this.maybeApplyPreflight();
      },
    },
    /**
     * The scan overlay is deliberately NOT bound to the one-shot
     * `preflightApplied` guard.
     *
     * `applyVerificationPreflight` commits `preflightApplied = true` whether or
     * not a scan was found, and `maybeApplyPreflight` early-returns on that
     * flag — so a catalogue that arrived even a millisecond late could never be
     * applied, and the workspace stayed in model-only mode forever. The scan
     * fetch is asynchronous and independent of the model/tag gates, so it needs
     * its own trigger.
     *
     * `applyScanOverlay` is idempotent (guards on `_appliedScanName`), so it is
     * safe for both this watcher and the pre-flight to call it.
     */
    selectedScan: {
      immediate: true,
      handler(scan, previous) {
        if (previous?.name && previous.name !== scan?.name) {
          this.removeScanAppearance(previous);
        }
        if (scan?.name) this.applyScanAppearance(scan);
      },
    },
    visualizationSettings: {
      deep: true,
      immediate: true,
      handler() {
        this.applyVisualizationSettings();
      },
    },
    /**
     * Deliberately its own watcher rather than a branch inside
     * `applyVisualizationSettings`: that method hangs off the `deep` watcher
     * above, which the opacity slider re-fires on every `@input` — once per
     * pixel of drag. Drawing overlay geometry from there would rebuild the triad
     * hundreds of times per drag. This fires only when the flag itself flips.
     *
     * Not `immediate`: at mount there is no crop box to anchor to. Preflight
     * owns the first draw; this watcher owns every toggle after it.
     */
    showAxes(show) {
      if (show) this.applyAxisTriad();
      else removeAxisTriad(this.$store.state.viewer);
    },
    /**
     * Opacity re-tints the live materials — no rebuild, so this is safe even
     * though its slider rides `@input` and fires once per pixel of drag.
     */
    axesOpacity(value) {
      setAxisTriadOpacity(this.$store.state.viewer, value);
    },
    /**
     * Size changes geometry, so it rebuilds. Affordable only because the Size
     * slider commits on `@change` (release) rather than `@input` — one rebuild
     * per drag, not one per pixel. Both this and the opacity watcher stay OUT of
     * the deep `visualizationSettings` watcher for that same reason.
     */
    axesSize() {
      if (this.showAxes) this.applyAxisTriad();
    },
    /**
     * `drawAxisTriad` refuses to draw without a unit transform — every one of
     * its dimensions is derived from a metre constant. Preflight normally has
     * one by the time it draws, but a model that resolves its geometry before it
     * can state its units would otherwise leave the triad permanently absent
     * with only a debug line to say so.
     */
    unitTransform(next) {
      if (next && this.showAxes) this.applyAxisTriad();
    },
    markerModels: {
      deep: true,
      immediate: true,
      handler() {
        this.scheduleMarkerProjection();
      },
    },
    modelReady(ready) {
      if (!ready) return;
      this.attachMarkerListeners();
      this.scheduleMarkerProjection();
      this.applyVisualizationSettings();
    },
  },
  created() {
    // Registered before mount so an arm request that arrives while the Forge
    // model is still downloading is still heard (it will answer with FAILED
    // until there's a canvas, which is the honest response).
    EventBus.$on(MODEL_PICK_EVENTS.ARM, this.armModelPick);
    EventBus.$on(MODEL_PICK_EVENTS.CANCEL, this.disarmModelPick);
  },
  mounted() {
    // Wait for the #viewer3d div to exist in the DOM — launchViewer queries
    // it via document.getElementById. $nextTick guarantees the template has
    // rendered before we dispatch.
    this.$nextTick(() => {
      this.startViewer().then(() => {
        this.attachMarkerListeners();
        this.scheduleMarkerProjection();
        this.applyVisualizationSettings();
      });
    });
  },
  beforeDestroy() {
    // Mark the instance as destroyed so any in-flight dispatch promise
    // resolved *after* unmount won't touch the DOM (the `.then` callback
    // uses global document.getElementById — without this guard, a slow
    // dispatch that resolves on the next route would inadvertently mutate
    // that route's DOM).
    this._isUnmounted = true;

    // Kill any outstanding pre-flight poll before teardown — its callback
    // reaches into the store and the viewer, both of which this route is about
    // to stop owning.
    this.clearPreflightRetry();
    this.detachMarkerListeners();

    // Remove the Forge GEOMETRY_LOADED_EVENT listener we registered in
    // startViewer. Symmetric add/remove — the bound handler ref was stashed
    // on `this._onGeometryLoadedBound` so we can pass the same function
    // reference to removeEventListener (otherwise it wouldn't match).
    const viewer = this.$store.state.viewer;
    if (viewer && this._onGeometryLoadedBound && window.Autodesk?.Viewing?.GEOMETRY_LOADED_EVENT) {
      viewer.removeEventListener?.(
        window.Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
        this._onGeometryLoadedBound,
      );
    }
    this._onGeometryLoadedBound = null;

    // Model-pick teardown. Two separate leaks to close:
    //   1. EventBus is a global Vue singleton — an un-removed listener would
    //      survive this route and fire into a destroyed component.
    //   2. Being destroyed WHILE armed is a real path (the surveyor navigates
    //      away mid-pick), so the canvas click listener must come off too.
    EventBus.$off(MODEL_PICK_EVENTS.ARM, this.armModelPick);
    EventBus.$off(MODEL_PICK_EVENTS.CANCEL, this.disarmModelPick);
    this.disarmModelPick();

    // Full verification-scoped cleanup BEFORE the DOM detach, so the next
    // viewer page inherits a clean viewer (no lingering isolation, no crop
    // box, no enabled scan overlay). The Forge viewer instance itself is
    // cached across routes via SET_PAGE_VIEW_3D — we only undo OUR changes
    // to its state, never dispose it.
    this.teardownVerificationPreflight();

    // Reset Vuex verification state so re-entry (same task or different)
    // starts from a known-clean slate.
    this.$store.commit('VERIFICATION/RESET');

    // Mirrors pageView.vue:3699 behavior — explicitly detach #forgeViewer
    // from our DOM before Vue unmounts us. The cached reference in Vuex
    // (SET_PAGE_VIEW_3D / pageView3d) stays alive so the next viewer page
    // can re-insert it.
    this.$el?.querySelector?.('#forgeViewer')?.remove();
  },
  methods: {
    applyVisualizationSettings() {
      if (this._isUnmounted || !this.visualizationSettings) return;
      this.applyModelAppearance();
      if (this.selectedScan?.name) this.applyScanAppearance(this.selectedScan);
    },
    applyModelAppearance() {
      const viewer = this.$store.state.viewer;
      const settings = this.visualizationSettings?.model;
      const model =
        this.$store.getters['VERIFICATION/GET_VERIFIED_MODEL'] || viewer?.model;
      if (!viewer || !model || !settings) return;

      const visible = settings.visible !== false;
      if (this._appliedModelVisibility !== visible) {
        try {
          setModelVisibility(viewer, model, visible);
          this._appliedModelVisibility = visible;
          if (visible) {
            const dbIds = this.$store.getters['VERIFICATION/GET_VERIFIED_DBIDS'];
            const mainExt = viewer.getExtension?.('MainExtension');
            if (dbIds?.length && typeof mainExt?.isolateDbIds === 'function') {
              mainExt.isolateDbIds(dbIds, model);
            }
            // Showing a model can rebuild its render state, including theming.
            this._appliedModelTintKey = null;
          }
        } catch (err) {
          debugError('[VerificationViewer] model visibility failed', err);
        }
      }
      if (!visible) return;

      const wireframe = settings.renderStyle === 'wireframe';
      try {
        viewer.setDisplayEdges?.(wireframe);
        const THREE = window.THREE;
        const impl = viewer.impl;
        if (impl && THREE?.Vector4) {
          if (wireframe) {
            if (!impl._envizionOrigEdgeColorMain && impl.edgeColorMain) {
              const original = impl.edgeColorMain;
              impl._envizionOrigEdgeColorMain = new THREE.Vector4(
                original.x ?? 0,
                original.y ?? 0,
                original.z ?? 0,
                original.w ?? 1,
              );
            }
            impl.edgeColorMain = new THREE.Vector4(0.95, 0.95, 0.95, 1);
          } else if (impl._envizionOrigEdgeColorMain) {
            impl.edgeColorMain = impl._envizionOrigEdgeColorMain;
          }
        }
        // Style and opacity are independent controls. "Outlined" adds edges;
        // it must not silently replace the opacity percentage shown in the UI.
        setModelTransparency(model, settings.opacity);
      } catch (err) {
        debugError('[VerificationViewer] model opacity/style failed', err);
      }

      const dbIds = this.$store.getters['VERIFICATION/GET_VERIFIED_DBIDS'] || [];
      const tintKey = [
        model.id,
        settings.tintEnabled,
        settings.color,
        dbIds.join(','),
      ].join('|');
      if (tintKey === this._appliedModelTintKey) return;

      try {
        viewer.clearThemingColors?.(model);
        if (settings.tintEnabled && dbIds.length && window.THREE?.Vector4) {
          const rgb = this.hexToRgb(settings.color);
          const color = new window.THREE.Vector4(rgb.r, rgb.g, rgb.b, 0.86);
          dbIds.forEach((dbId) => viewer.setThemingColor(dbId, color, model, true));
        }
        this._appliedModelTintKey = tintKey;
        viewer.impl?.invalidate?.(true, true, true);
      } catch (err) {
        debugError('[VerificationViewer] model tint failed', err);
      }
    },
    applyScanAppearance(scan) {
      if (this._isUnmounted || !scan?.name) return;
      const viewer = this.$store.state.viewer;
      const mainExt = viewer?.getExtension?.('MainExtension');
      const settings = this.visualizationSettings?.scan;
      if (!mainExt || !settings) return;

      mainExt.setPointCloudAppearance?.(scan.name, settings);
      if (settings.visible === false) {
        if (this._appliedScanName === scan.name) {
          setScanVisibility({
            store: this.$store,
            mainExt,
            name: scan.name,
            visible: false,
          });
        }
        return;
      }
      this.applyScanOverlay(scan);
    },
    removeScanAppearance(scan) {
      if (!scan?.name) return;
      const viewer = this.$store.state.viewer;
      const mainExt = viewer?.getExtension?.('MainExtension');
      mainExt?.clearPointCloudAppearance?.(scan.name);
      if (this._appliedScanName !== scan.name) return;
      this.disposeRetainedScan(scan.name);
    },
    disposeRetainedScan(name) {
      if (!name) return false;
      const viewer = this.$store.state.viewer;
      const mainExt = viewer?.getExtension?.('MainExtension');
      this.$store.commit('SET_POINT_CLOUD_SHOWN', { name, isShown: false });
      mainExt?.clearPointCloudAppearance?.(name);
      mainExt?.disposePointCloudByName?.(name);
      if (this._appliedScanName === name) this._appliedScanName = null;
      return true;
    },
    hexToRgb(hex) {
      const parsed = Number.parseInt(String(hex || '').replace('#', ''), 16);
      if (!Number.isFinite(parsed)) return { r: 1, g: 1, b: 1 };
      return {
        r: ((parsed >> 16) & 255) / 255,
        g: ((parsed >> 8) & 255) / 255,
        b: (parsed & 255) / 255,
      };
    },
    isWorldPoint(point) {
      return !!point
        && Number.isFinite(point.x)
        && Number.isFinite(point.y)
        && Number.isFinite(point.z);
    },
    scheduleMarkerProjection() {
      if (this._isUnmounted || this._markerProjectionFrame) return;
      this._markerProjectionFrame = window.requestAnimationFrame(() => {
        this._markerProjectionFrame = null;
        this.projectMarkers();
      });
    },
    projectMarkers() {
      const viewer = this.$store.state.viewer;
      const THREE = window.THREE;
      const width = this.$el?.clientWidth || viewer?.container?.clientWidth || 0;
      const height = this.$el?.clientHeight || viewer?.container?.clientHeight || 0;
      if (!viewer || !THREE?.Vector3 || !width || !height || !this.markerSettings.visible) {
        this.projectedMarkers = [];
        return;
      }

      // `worldToClient` clones and matrix-multiplies its argument, so the
      // error-vector util cannot build its own probe positions — it takes this
      // factory instead, which is what keeps it free of viewer globals and
      // assertable under node.
      const makeVector = (x, y, z) => new THREE.Vector3(x, y, z);

      // Dev-only space check, ONCE per projection pass rather than per marker.
      // Every marker here is built from the same store points, so one sample
      // answers for the batch — and this method runs on an animation frame, so a
      // call inside the loop would be N warnings per frame for one mistake.
      assertViewerPoint(
        this.markerModels[0]?.point,
        this.$store.getters['VERIFICATION/GET_VERIFIED_BBOX'] || this.unitTransform,
      );

      this.projectedMarkers = this.markerModels.reduce((projected, marker) => {
        try {
          const client = viewer.worldToClient(
            new THREE.Vector3(marker.point.x, marker.point.y, marker.point.z),
          );
          if (!client || !Number.isFinite(client.x) || !Number.isFinite(client.y)) return projected;
          // Cull margin, the one size-coupled number in this method: an anchor
          // just outside the viewport can still have visible marker on screen,
          // and at 2x the shape's own half-extent (~28px) reaches the old fixed
          // margin exactly. Scaling it keeps the cull no tighter than it has
          // always been. The projection itself — worldToClient → left/top — is
          // fully size-independent.
          const margin = 28 * this.markerScale;
          if (client.x < -margin || client.x > width + margin
            || client.y < -margin || client.y > height + margin) return projected;
          // The vectors are built COMPLETE here, before the entry is pushed.
          // Vue 2 cannot observe a property added to an object it is already
          // observing, so resolving directions in a second pass over
          // `projectedMarkers` would leave the arrows frozen at whatever the
          // camera was doing on the first frame.
          projected.push({
            ...marker,
            x: client.x,
            y: client.y,
            vectors: this.projectMarkerVectors(marker, viewer, makeVector),
          });
        } catch (err) {
          debugWarn('[VerificationViewer] marker projection skipped', err);
        }
        return projected;
      }, []);

      this.logErrorVectors();
    },
    /**
     * A marker's deviation arrows, with each axis' screen direction resolved
     * for the camera as it stands.
     *
     * The util returns UNSIGNED basis directions (a property of the camera
     * alone, computed for all three axes together so degeneracy stays a
     * relative test); the deviation's sign is applied here, which is what makes
     * an arrow point the way the support actually moved.
     */
    projectMarkerVectors(marker, viewer, makeVector) {
      if (!marker.vectors?.length) return [];
      const directions = computeAxisScreenVectors(
        viewer,
        marker.point,
        this.vectorEpsilon,
        makeVector,
      );
      return marker.vectors.map((vector) => {
        const direction = directions[vector.axis];
        const sign = vector.deltaMm < 0 ? -1 : 1;
        return {
          ...vector,
          dx: direction.dx * sign,
          dy: direction.dy * sign,
          degenerate: direction.degenerate,
        };
      });
    },
    /**
     * Log the error-vector state, but only when it CHANGES.
     *
     * `projectMarkers` runs on every camera frame, so an unguarded log here
     * would emit tens of lines a second and bury everything else in the
     * console. The signature covers exactly what is worth knowing — which axes
     * drew, at what value, in which band, and whether the camera could resolve
     * a direction for them — so every change that matters still produces a
     * line, and a plain orbit produces none.
     */
    logErrorVectors() {
      const withVectors = this.projectedMarkers.filter((marker) => marker.vectors?.length);
      const signature = withVectors
        .map((marker) => `${marker.pointId}=${marker.vectors
          .map((v) => `${v.axis}${v.valueMm}${v.severity}${v.degenerate ? '.flat' : ''}`)
          .join(',')}`)
        .join(' ');
      if (signature === this._loggedVectorSignature) return;
      this._loggedVectorSignature = signature;
      if (!withVectors.length) return;

      debugLog('[VerificationViewer] error vectors', {
        points: withVectors.length,
        epsilon: this.vectorEpsilon,
        vectors: signature,
      });

      // The honest-fallback path: an axis with no direction on this projection
      // (the depth axis of an orthographic view) is drawn as a dot plus its
      // value rather than an arrow pointing somewhere arbitrary.
      const flat = withVectors.filter((marker) => marker.vectors.some((v) => v.degenerate));
      if (flat.length) {
        debugLog('[VerificationViewer] error vectors: no screen direction, drawn as dot + value', {
          points: flat.map((marker) => marker.pointId),
          axes: flat.flatMap((marker) => marker.vectors
            .filter((v) => v.degenerate)
            .map((v) => v.axis)),
        });
      }
    },
    attachMarkerListeners() {
      const viewer = this.$store.state.viewer;
      if (!viewer || this._markerViewer === viewer) return;
      this.detachMarkerListeners();
      this._markerViewer = viewer;
      this._onMarkerCameraChangeBound = this.scheduleMarkerProjection.bind(this);
      this._markerEventNames = [
        window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT,
        window.Autodesk?.Viewing?.VIEWER_RESIZE_EVENT,
      ].filter(Boolean);
      this._markerEventNames.forEach((eventName) => {
        viewer.addEventListener?.(eventName, this._onMarkerCameraChangeBound);
      });
      window.addEventListener('resize', this._onMarkerCameraChangeBound);
    },
    detachMarkerListeners() {
      if (this._markerProjectionFrame) {
        window.cancelAnimationFrame(this._markerProjectionFrame);
        this._markerProjectionFrame = null;
      }
      if (this._markerViewer && this._onMarkerCameraChangeBound) {
        (this._markerEventNames || []).forEach((eventName) => {
          this._markerViewer.removeEventListener?.(eventName, this._onMarkerCameraChangeBound);
        });
        window.removeEventListener('resize', this._onMarkerCameraChangeBound);
      }
      this._markerViewer = null;
      this._markerEventNames = [];
      this._onMarkerCameraChangeBound = null;
      this.projectedMarkers = [];
    },
    // Line-for-line mirror of ViewersSection.vue's initialize3dViewer()
    // (src/features/Dashboard/components/ViewersSection.vue:979-1010).
    //   1. Prime assetId + viewerState.isLoading when there's no viewer yet
    //      so the loader overlay shows immediately (before launchViewer's
    //      own internal flag flip).
    //   2. Dispatch START_VIEWER_3D — action internally routes to either
    //      launchViewer (fresh) or RESTORE_3D_VIEWER (short-circuit).
    //   3. Unconditionally: remove any stray #forgeViewer in the current
    //      document, grab our #viewer3d parent, insert the cached pageView3d
    //      node. This works for BOTH paths — on fresh launch the removed
    //      node is the same one we insert back (effectively a no-op), on
    //      restore the cached node lands in our mount point.
    async startViewer() {
      if (!this.$store.state.viewer) {
        this.$store.state.assetId = this.$route.params.id;
        this.$store.state.viewerState.isLoading = true;
      }

      const area = this.$store.getters['AREAS/GET_SELECTED_AREA'];

      debugLog('[VerificationViewer] dispatching START_VIEWER_3D');
      this.$store.commit('VERIFICATION/SET_VIEWER_ERROR', null);
      return this.$store
        .dispatch('START_VIEWER_3D', { area })
        .then(() => {
          // Guard against a dispatch that resolved after the user already
          // navigated away — without this, we'd mutate whatever #viewer3d
          // the NEW route happens to have, which is the bug symptom the
          // user originally reported (3D viewer bleeding into 2D pages).
          if (this._isUnmounted) return;

          document.getElementById('forgeViewer')?.remove();

          const parentNode = document.getElementById('viewer3d');
          if (!parentNode) return;

          if (parentNode.firstChild) {
            parentNode.insertBefore(this.pageView3d, parentNode.firstChild);
          } else {
            parentNode.appendChild(this.pageView3d);
          }

          debugLog('[VerificationViewer] viewer ready');

          // Path 2 of 2: register a GEOMETRY_LOADED_EVENT listener on the
          // live viewer. This is the ONLY reliable signal that
          // viewer.model.getInstanceTree() is usable — it's an external
          // Forge object, so Vue reactivity can't observe it. The bound
          // handler ref is stashed so beforeDestroy can remove it cleanly.
          const viewer = this.$store.state.viewer;
          if (viewer && window.Autodesk?.Viewing?.GEOMETRY_LOADED_EVENT) {
            this._onGeometryLoadedBound = this._onGeometryLoaded.bind(this);
            viewer.addEventListener(
              window.Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
              this._onGeometryLoadedBound,
            );
            // If geometry already loaded by the time we get here (e.g. cached
            // model re-entry), the event has already fired — check synchronously.
            if (viewer.model?.getInstanceTree?.()) {
              debugLog('[VerificationViewer] instanceTree already ready — firing preflight sync');
              this._onGeometryLoadedBound();
            }
          }
        })
        .catch((err) => {
          const message = err?.message || 'Viewer failed to load';
          this.$store.commit('VERIFICATION/SET_VIEWER_ERROR', message);
          debugError('[VerificationViewer] START_VIEWER_3D failed', err);
        })
        .finally(() => {
          // Reset the loading flag on every terminal path — matches
          // ViewersSection.vue:1009-1011. On success, launchViewer's
          // progressCallback already flips isLoading to false at 100%;
          // on failure (no master URN, network error, 404) this is the
          // only reset, so the loader overlay doesn't stay forever.
          this.$store.state.viewerState.isLoading = false;
        });
    },
    // ---- Armed model pick (AVA-475 Task 4) --------------------------------
    //
    // One-shot: the next click that actually lands on model geometry resolves
    // to a world XYZ, disarms, and reports the point. A click that MISSES the
    // geometry leaves the pick armed so the surveyor just clicks again — the
    // same forgiving behaviour as `utils/cloudPick.js:101`.

    /**
     * Arm the one-shot pick. Idempotent — re-arming replaces the previous
     * listener rather than stacking a second one.
     */
    armModelPick() {
      if (this._isUnmounted) return;
      this.disarmModelPick();

      const viewer = this.$store.state.viewer;
      const target = viewer?.canvas || viewer?.container;
      if (!viewer || !target) {
        debugWarn('[VerificationViewer] cannot arm model pick — no viewer canvas');
        EventBus.$emit(MODEL_PICK_EVENTS.FAILED, {
          reason: 'The 3D viewer is not ready for picking yet.',
        });
        return;
      }

      this._modelPickTarget = target;
      this._onModelPickClickBound = this._onModelPickClick.bind(this);
      // Drag guard: the browser fires `click` on mouseup however far the pointer
      // travelled, so orbiting the camera while armed would otherwise capture a
      // point at the release position. Track the press so the release can be
      // compared against it — same reason MeasurementExtension.js:32-33 binds
      // mousedown alongside click.
      this._onModelPickDownBound = this._onModelPickDown.bind(this);
      target.addEventListener('mousedown', this._onModelPickDownBound);
      target.addEventListener('click', this._onModelPickClickBound);

      // Crosshair affordance, mirroring MeasurementExtension's cursor handling
      // (src/extensions/MeasurementExtension.js:176). The previous value is
      // stashed so disarming restores exactly what was there.
      this._modelPickPrevCursor = target.style.cursor;
      target.style.cursor = 'crosshair';

      debugLog('[VerificationViewer] model pick armed');
    },
    /**
     * Detach the armed pick and restore the cursor. Safe to call at any time,
     * including when nothing is armed — called from the success path, the
     * CANCEL channel, and beforeDestroy.
     */
    disarmModelPick() {
      const target = this._modelPickTarget;
      if (target && this._onModelPickClickBound) {
        target.removeEventListener('click', this._onModelPickClickBound);
        if (this._onModelPickDownBound) {
          target.removeEventListener('mousedown', this._onModelPickDownBound);
        }
        target.style.cursor = this._modelPickPrevCursor || '';
        debugLog('[VerificationViewer] model pick disarmed');
      }
      this._modelPickTarget = null;
      this._onModelPickClickBound = null;
      this._onModelPickDownBound = null;
      this._modelPickDownAt = null;
      this._modelPickPrevCursor = null;
    },
    /** Records where the press started, so a drag can be told from a click. */
    _onModelPickDown(event) {
      this._modelPickDownAt = { x: event.clientX, y: event.clientY };
    },
    /** Canvas click handler while armed. */
    _onModelPickClick(event) {
      if (this._isUnmounted) return;
      // A camera drag ends in a click — ignore it and stay armed, matching the
      // existing "missed geometry" behaviour rather than capturing a bad point.
      const downAt = this._modelPickDownAt;
      if (downAt) {
        const dx = event.clientX - downAt.x;
        const dy = event.clientY - downAt.y;
        if (Math.sqrt(dx * dx + dy * dy) > MODEL_PICK_DRAG_THRESHOLD_PX) {
          debugLog('[VerificationViewer] drag ignored — model pick still armed');
          return;
        }
      }
      const point = this.resolveModelPointAt(event);
      if (!point) {
        debugWarn('[VerificationViewer] click missed model geometry — pick still armed');
        return;
      }
      this.disarmModelPick();
      const pointId = this.$store.state.VERIFICATION.activePointId;
      if (pointId) {
        this.$store.commit('VERIFICATION/SET_DESIGN_PICK_PREVIEW', { pointId, point });
      }
      debugLog('[VerificationViewer] model pick', point);
      this.$emit('model-pick', point);
      EventBus.$emit(MODEL_PICK_EVENTS.RESULT, point);
    },
    /**
     * Resolve a click to a point on the model surface, in WORLD space.
     *
     * COORDINATE SPACE — this must match `utils/cloudPick.js` exactly, because
     * the deltas subtract one from the other (`d = asBuilt − design`). cloudPick
     * returns the raw intersection point with `matrixWorld` applied and NO
     * `viewerToGlobal` conversion, so we stay in the same viewer world space
     * here and apply no scaling either.
     *
     * WHY `clientToWorld` — it is this repo's established 3D pick API:
     * `PivotSetterTool.js:53` and `labels3dExt.js:1119` both resolve a 3D click
     * this way and read `.point`. (MeasurementExtension uses the Snapper for 3D
     * instead, but that is because it wants vertex/edge SNAPPING, a different
     * requirement — its `clientToWorld` call at line 239 is the 2D path.)
     *
     * It expects CANVAS-relative pixels — Forge's `clientToViewport` divides by
     * the canvas rect — so `clientX/Y` are converted first. Every in-repo caller
     * passes `event.offsetX/offsetY`; deriving from the canvas rect is
     * equivalent when the canvas is the event target and stays correct when it
     * is not.
     *
     * ⚠ UNVERIFIED IN ORTHOGRAPHIC: preflight puts the camera in ortho (see
     * `applyVerificationPreflight` below), and the Task 0 spike flagged
     * ortho-safe ray construction as the non-obvious risk. Accuracy under ortho
     * is part of the same live-leg check still owed for `cloudPick.js`.
     *
     * @returns {{x:number,y:number,z:number}|null} null when the click missed.
     */
    resolveModelPointAt(event) {
      const viewer = this.$store.state.viewer;
      const canvas = viewer?.canvas || viewer?.container;
      if (!viewer || !canvas) return null;

      const rect = canvas.getBoundingClientRect();
      const canvasX = event.clientX - rect.left;
      const canvasY = event.clientY - rect.top;

      try {
        // 3rd arg is `ignoreTransparent`, and it MUST be false here. Every 3D
        // caller in this repo passes false (PivotSetterTool.js:53;
        // labels3dExt.js:1118, inside an explicit `!isViewer2d()` branch) —
        // only the 2D paths pass true. It matters concretely: the status bar's
        // Wireframe style drops this very model to 0.1 opacity
        // (ViewerStatusBar.vue:439), so `true` would skip the support the
        // surveyor is aiming at and silently return a point on whatever sits
        // behind it — a wrong-but-plausible coordinate.
        const hit = viewer.clientToWorld?.(canvasX, canvasY, false);
        if (hit?.point) return { x: hit.point.x, y: hit.point.y, z: hit.point.z };

        // Not a miss-recovery path — `clientToWorld` delegates to `hitTest`, so
        // a miss stays a miss. This only covers a viewer build that does not
        // expose `clientToWorld` at all.
        const implHit = viewer.impl?.hitTest?.(canvasX, canvasY, false);
        const raw = implHit?.intersectPoint || implHit?.point;
        if (raw) return { x: raw.x, y: raw.y, z: raw.z };
      } catch (err) {
        debugError('[VerificationViewer] model pick hit-test failed', err);
      }
      return null;
    },
    /**
     * Forge GEOMETRY_LOADED_EVENT handler. Records that the viewer's
     * instanceTree is usable and tries to apply preflight.
     */
    _onGeometryLoaded() {
      if (this._isUnmounted) return;
      debugLog('[VerificationViewer] GEOMETRY_LOADED_EVENT fired');
      this.$store.commit('VERIFICATION/SET_MODEL_READY', true);
      this.maybeApplyPreflight();
    },
    /**
     * Single gate that fires preflight iff BOTH signals are in:
     *   - `modelReady` (from GEOMETRY_LOADED_EVENT — external Forge event)
     *   - `verifiedDbIds.length > 0` (from Vuex verifiedTags load)
     *
     * Both paths (event handler + dbIds watcher) call this. The first call
     * that finds both signals satisfied wins; subsequent calls are no-ops
     * thanks to the `preflightApplied` guard.
     */
    maybeApplyPreflight() {
      if (this.preflightApplied || this._isUnmounted) return;
      if (!this.modelReady) return;
      const dbIds = this.$store.getters['VERIFICATION/GET_VERIFIED_DBIDS'];
      if (!dbIds?.length) return;
      this.applyVerificationPreflight();
    },

    /**
     * Re-attempt the pre-flight while its viewer-derived inputs are still
     * resolving. Bounded by PREFLIGHT_MAX_ATTEMPTS; after that we surface a
     * viewer error rather than leaving the workspace silently inert.
     *
     * Only one timer is ever outstanding, so the two entry paths (event handler
     * and dbIds watcher) cannot stack up competing polls.
     */
    schedulePreflightRetry() {
      if (this._isUnmounted || this.preflightApplied) return;
      if (this._preflightRetryTimer) return;

      this._preflightAttempts = (this._preflightAttempts || 0) + 1;
      if (this._preflightAttempts > PREFLIGHT_MAX_ATTEMPTS) {
        debugWarn('[VerificationViewer] preflight gave up — tag bbox never resolved', {
          attempts: this._preflightAttempts,
        });
        this.$store.commit(
          'VERIFICATION/SET_VIEWER_ERROR',
          'Could not resolve the tag geometry. Try reloading the page.',
        );
        return;
      }

      this._preflightRetryTimer = setTimeout(() => {
        this._preflightRetryTimer = null;
        this.maybeApplyPreflight();
      }, PREFLIGHT_RETRY_MS);
    },

    /** Stop any outstanding pre-flight poll and reset the attempt counter. */
    clearPreflightRetry() {
      if (this._preflightRetryTimer) {
        clearTimeout(this._preflightRetryTimer);
        this._preflightRetryTimer = null;
      }
      this._preflightAttempts = 0;
    },
    /**
     * Apply the verification pre-flight: isolate dbIds → crop to bbox+20% →
     * frame camera → overlay best-matching scan.
     *
     * Called only from `maybeApplyPreflight` once both readiness gates
     * have flipped. Sets `preflightApplied = true` on success (reactive,
     * drives the loading overlay to dismiss).
     */
    applyVerificationPreflight() {
      const viewer = this.$store.state.viewer;
      if (!viewer || this._isUnmounted) return;

      const store = this.$store;
      const dbIds = store.getters['VERIFICATION/GET_VERIFIED_DBIDS'];
      // Resolve BEFORE reading the derived getters. The geometry cannot be
      // derived reactively (the viewer and its instance trees are invisible to
      // Vue), so this imperative refresh is what makes the retry loop meaningful
      // — without it every attempt would re-read the same cached miss.
      //
      // The action body is synchronous, so its commit has already landed by the
      // time dispatch() returns — but dispatch() itself resolves to a Promise,
      // so read the value back through the getter rather than from its return.
      store.dispatch('VERIFICATION/RESOLVE_VERIFIED_GEOMETRY');
      const geometry = store.getters['VERIFICATION/GET_VERIFIED_GEOMETRY'];
      const bbox = store.getters['VERIFICATION/GET_VERIFIED_BBOX'];
      const cropBox = store.getters['VERIFICATION/GET_VERIFIED_CROP_BOX'];
      const scan = store.getters['VERIFICATION/GET_SELECTED_SCAN'];

      if (!dbIds?.length || !bbox || !cropBox) {
        // Log the model census too — an empty bbox is nearly always either
        // "no model has loaded geometry yet" or "the dbIds belong to a model
        // that isn't loaded", and the census tells those apart at a glance.
        debugLog('[VerificationViewer] preflight skipped — missing inputs', {
          attempt: (this._preflightAttempts || 0) + 1,
          dbIds,
          hasBbox: !!bbox,
          hasCropBox: !!cropBox,
          models: listLoadedModels(viewer).map((m) => ({
            id: m.id,
            isCurrent: m === viewer.model,
            hasTree: !!m.getInstanceTree?.(),
          })),
        });
        // Transient: fragments may still be resolving. Nothing observable will
        // re-trigger us, so poll rather than abandon the workspace.
        this.schedulePreflightRetry();
        return;
      }
      this.clearPreflightRetry();

      const mainExt = viewer.getExtension?.('MainExtension');
      const cropExt = viewer.getExtension?.('Cropping3DExtension');

      // 1. Isolate the verified tag's geometry.
      //
      //    Target the model that actually OWNS the geometry, not `viewer.model`.
      //    Several models are loaded on this route (the NWD plus an empty `{3D}`)
      //    and dbIds are per-model, so isolating against the current model can
      //    silently address the wrong one.
      const verifiedModel = store.getters['VERIFICATION/GET_VERIFIED_MODEL'] || viewer.model;
      if (mainExt && typeof mainExt.isolateDbIds === 'function') {
        debugLog('[VerificationViewer] isolating dbIds', {
          dbIds,
          modelId: verifiedModel?.id,
          usedCurrentModel: verifiedModel === viewer.model,
          fragCount: geometry?.fragCount ?? 0,
        });
        mainExt.isolateDbIds(dbIds, verifiedModel);
      } else {
        debugError('[VerificationViewer] MainExtension not available — skipping isolate');
      }

      // 2. Apply crop box at bbox × 1.20 (+20% margin) so deviation between
      //    design and as-built stays in frame.
      //
      //    Four-step pattern matching tagsList.vue:741-743:
      //      setCropBox           → clone bbox, propagate to Forge + point clouds
      //      toggleCropbox(true)  → flip croppingOn=true, commit isCropBoxVisible=true
      //      setCropBoxControls   → redraw the visual handles on the computed bbox
      //      updateBoxVisibility(false) → hide the visual chrome (mesh + arrows)
      //        without clearing the crop itself. Clipping effect stays active,
      //        user can re-reveal the controls via the status bar's Crop toggle.
      //    `updateBoxVisibility` requires `croppingOn=true` (early-returns otherwise),
      //    which is why the `toggleCropbox(true)` step is mandatory — without it
      //    the status-bar toggle would silently no-op.
      if (cropExt && typeof cropExt.setCropBox === 'function') {
        debugLog('[VerificationViewer] applying crop box (+20% margin)');
        cropExt.setCropBox(cropBox);
        if (typeof cropExt.toggleCropbox === 'function') {
          cropExt.toggleCropbox(true);
        }
        if (typeof cropExt.setCropBoxControls === 'function') {
          cropExt.setCropBoxControls(cropBox);
        }
        if (typeof cropExt.updateBoxVisibility === 'function') {
          cropExt.updateBoxVisibility(false);
        }
      } else {
        debugError('[VerificationViewer] Cropping3DExtension not available — skipping crop');
      }

      // 3. Set the verification-workflow camera defaults:
      //    - Orthographic projection (no foreshortening for tolerance reads)
      //    - Isometric view as the default-on-load spatial context
      //    Both commits and the setView call use the shared utility so the
      //    status bar's "click Iso" path and this default-on-load path stay
      //    in lockstep. Per-check default views (Y-Pos → Plan etc.) are a
      //    follow-up; MVP lands on Iso until a check is selected.
      try {
        viewer.navigation?.toOrthographic?.();
      } catch (err) {
        debugError('[VerificationViewer] toOrthographic failed', err);
      }
      try {
        const pose = computeViewPose('iso', cropBox);
        if (pose && viewer.navigation?.setView) {
          viewer.navigation.setView(pose.position, pose.target, pose.upVector);
        }
        viewer.navigation?.fitBounds?.(false, cropBox);
        store.commit('VERIFICATION/SET_ACTIVE_VIEW', 'iso');
        store.commit('VERIFICATION/SET_ACTIVE_CAPTURE_PLANE', PLANE_3D);
      } catch (err) {
        debugError('[VerificationViewer] default view apply failed', err);
      }

      // 4. Overlay the selected scan (combined preferred, individual fallback).
      //    `scan` may legitimately be null here: the catalogue fetch is async
      //    and independent of the model/tag gates this method waits on. That is
      //    NOT model-only mode — the `selectedScan` watcher applies the overlay
      //    whenever the catalogue resolves. This is not the last chance.
      if (scan?.name) {
        this.applyScanAppearance(scan);
      } else {
        debugLog('[VerificationViewer] no scan yet — awaiting scan catalogue');
      }

      // 5. Orientation triad, anchored on the ORIGINAL support crop box — the
      //    same `cropBox` applied in step 2, never the measurement box
      //    SliceController later narrows. A deviation reported as "+4 mm" is
      //    meaningless until the surveyor can see which way +X points, and a
      //    reference that moved with the slice would be worse than none.
      if (this.showAxes) this.applyAxisTriad();

      // Isolation clears theming and model visibility can rebuild material
      // state. Re-apply the shared controls after preflight owns the final
      // model/scan composition.
      this._appliedModelTintKey = null;
      this.applyVisualizationSettings();
      this.$store.commit('VERIFICATION/SET_PREFLIGHT_APPLIED', true);
      debugLog('[VerificationViewer] preflight applied');
    },

    /**
     * Show `scan` as the point-cloud overlay.
     *
     * Split out of `applyVerificationPreflight` so it is reachable from the
     * `selectedScan` watcher too — the pre-flight runs once and commits
     * `preflightApplied`, so binding the overlay to it meant a late-arriving
     * catalogue could never be applied.
     *
     * Idempotent: re-showing the scan already on screen is a no-op, so both
     * callers can fire freely. Safe to call before the viewer exists — the
     * pre-flight will call again once it does.
     */
    applyScanOverlay(scan) {
      if (this._isUnmounted || !scan?.name) return;

      const viewer = this.$store.state.viewer;
      const mainExt = viewer?.getExtension?.('MainExtension');
      if (typeof mainExt?.showPointCloud !== 'function') {
        debugLog('[VerificationViewer] scan overlay deferred — viewer not ready', {
          name: scan.name,
        });
        return;
      }

      if (this._appliedScanName === scan.name) {
        setScanVisibility({
          store: this.$store,
          mainExt,
          name: scan.name,
          visible: true,
          url: scan.url,
        });
        return;
      }

      // A different scan was showing (tag or facility switched). Take ours down
      // first, scoped by `_appliedScanName`, so we never orphan an overlay or
      // touch a scan the user enabled from another surface.
      if (this._appliedScanName) {
        this.disposeRetainedScan(this._appliedScanName);
      }

      debugLog('[VerificationViewer] showing scan overlay', { name: scan.name });
      if (setScanVisibility({
        store: this.$store,
        mainExt,
        name: scan.name,
        visible: true,
        url: scan.url,
      })) {
        this._appliedScanName = scan.name;
      }
    },
    /**
     * Draw the axis triad on the verified support's crop box.
     *
     * Called from preflight and from the `showAxes` watcher. `drawAxisTriad` is
     * idempotent on the same box, so both callers can fire freely. Silently does
     * nothing before the crop box resolves — preflight calls again once it has.
     */
    applyAxisTriad() {
      if (this._isUnmounted) return;
      const viewer = this.$store.state.viewer;
      const cropBox = this.$store.getters['VERIFICATION/GET_VERIFIED_CROP_BOX'];
      if (!viewer || !cropBox) {
        debugLog('[VerificationViewer] axis triad skipped — no viewer or crop box', {
          hasViewer: !!viewer, hasCropBox: !!cropBox,
        });
        return;
      }
      drawAxisTriad(viewer, cropBox, {
        sizeScale: this.axesSize,
        opacity: this.axesOpacity,
        unitTransform: this.unitTransform,
      });
    },
    /**
     * Undo the verification pre-flight when leaving the page.
     *
     * The Forge viewer instance persists across routes (cached via
     * SET_PAGE_VIEW_3D) so isolation, crop, and the scan overlay we enabled
     * would leak to the next viewer page unless explicitly cleaned up here.
     *
     * Guarded with `if (ext)` checks so partial-loads (user leaves before
     * extensions finish loading) are safe no-ops.
     */
    teardownVerificationPreflight() {
      const viewer = this.$store.state.viewer;

      // Ahead of the no-viewer guard on purpose. The triad holds canvas
      // textures and materials that must be released even on the path where the
      // route is torn down after the viewer reference has already gone.
      // `removeAxisTriad` falls back to the viewer it drew into.
      removeAxisTriad(viewer);

      if (!viewer) return;

      const mainExt = viewer.getExtension?.('MainExtension');
      const cropExt = viewer.getExtension?.('Cropping3DExtension');

      // 1. Restore full geometry (undo isolate).
      try {
        mainExt?.endIsolation?.();
      } catch (err) {
        debugError('[VerificationViewer] endIsolation failed', err);
      }

      // 2. Remove crop box. Cropping3DExtension has no `clearCropBox` method —
      //    `toggleCropbox(false)` is the proper teardown: it clears cropBox,
      //    removes all visual chrome, and commits isCropBoxVisible=false.
      try {
        cropExt?.toggleCropbox?.(false);
      } catch (err) {
        debugError('[VerificationViewer] toggleCropbox(false) failed', err);
      }

      // 3. Hide the scan WE turned on — scoped by `_appliedScanName` so we
      //    don't touch scans the user enabled via other surfaces.
      if (this.selectedScan?.name) {
        mainExt?.clearPointCloudAppearance?.(this.selectedScan.name);
      }
      if (this._appliedScanName && this._appliedScanName !== this.selectedScan?.name) {
        mainExt?.clearPointCloudAppearance?.(this._appliedScanName);
      }
      if (this._appliedScanName) {
        this.disposeRetainedScan(this._appliedScanName);
      }

      // 4. Reset visual-style overrides so the next component mounting the
      //    cached viewer inherits a clean state, not our verification tweaks:
      //      - setDisplayEdges(false) — Wireframe mode may have enabled edges
      //      - edgeColorMain restored to the stashed original (ViewerStatusBar's
      //        setVisualStyle snapshots it on first Wireframe activation)
      try { viewer.setDisplayEdges?.(false); }
      catch (err) { debugError('[VerificationViewer] restore edges failed', err); }
      try {
        const verifiedModel =
          this.$store.getters['VERIFICATION/GET_VERIFIED_MODEL'] || viewer.model;
        viewer.clearThemingColors?.(verifiedModel);
        this._appliedModelTintKey = null;
      } catch (err) {
        debugError('[VerificationViewer] clear model tint failed', err);
      }
      try {
        if (viewer.impl?._envizionOrigEdgeColorMain) {
          viewer.impl.edgeColorMain = viewer.impl._envizionOrigEdgeColorMain;
          delete viewer.impl._envizionOrigEdgeColorMain;
          viewer.impl.invalidate?.(true, true, true);
        }
      } catch (err) { debugError('[VerificationViewer] restore edge color failed', err); }

      // 5. Restore user-mutable viewer state the status bar may have changed,
      //    so the next route (Dashboard, another viewer page) inherits a
      //    clean viewer rather than our overrides.
      //
      //    - Model visible: user may have turned the 3D layer off.
      //    - Full opacity: Wireframe mode sets 0.1 fill; restore to 1.0.
      //    - Perspective camera: user may have flipped to orthographic.
      //
      //    All calls are safe no-ops when state already matches default.
      if (viewer.model) {
        try { setModelVisibility(viewer, viewer.model, true); }
        catch (err) { debugError('[VerificationViewer] restore visibility failed', err); }
        try { setModelTransparency(viewer.model, 1.0); }
        catch (err) { debugError('[VerificationViewer] restore opacity failed', err); }
      }
      try { viewer.navigation?.toPerspective?.(); }
      catch (err) { debugError('[VerificationViewer] restore camera failed', err); }
      // Reset camera framing to the whole-model default so the next component
      // mounting this cached viewer inherits Forge-default framing rather
      // than our verified-tag crop angle.
      try { viewer.fitToView?.(); }
      catch (err) { debugError('[VerificationViewer] fitToView failed', err); }

      this.$store.commit('VERIFICATION/SET_PREFLIGHT_APPLIED', false);
      this.$store.commit('VERIFICATION/SET_MODEL_READY', false);

      debugLog('[VerificationViewer] preflight teardown complete');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.verification-viewer {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: $vf-canvas-bg;

  &__mount {
    width: 100%;
    height: 100%;
    position: relative; // anchor for v-overlay absolute positioning
  }

  // Empty state — mirrors the old ViewerPlaceholder's isometric-cube mark.
  // Only shown when the viewer hasn't started loading yet (e.g. cold
  // deep-link before URN resolves).
  &__empty {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #6b8a87;
    user-select: none;
    pointer-events: none;
    z-index: 1;
  }

  &__empty-icon {
    display: block;
  }

  &__empty-caption {
    font-family: $vf-font-heading;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8aa3a0;
    margin-top: 4px;
  }

  &__empty-sub {
    font-family: $vf-font-mono;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #5a7370;
  }
}

/* Marker geometry, every dimension derived from one multiplier.
 *
 * The literals below are the CALIBRATED base — 1.5x the pre-2026-07-31 values,
 * because the user found the original size too small. Baking the bump in here
 * rather than defaulting the slider to 1.5 keeps `markerSize: 1` meaning "the
 * approved size", so the control reads as a clean multiplier around it. Same
 * philosophy as the axis-triad rebase.
 *
 *   old -> base   old -> base
 *   shape    13 -> 19.5    as-built  12 -> 18
 *   border    3 -> 4.5     slice     10 -> 15
 *   chip font 9 -> 13.5    chip x    14 -> 21
 *   label nudge 13 -> 19.5 crosshair 18 -> 27
 *
 * Sized with calc(), NOT transform: scale(). `getComputedStyle` resolves calc()
 * to used pixels, so html2canvas rasterizes evidence captures at the real size
 * and the chip text stays crisp — a transform would rasterize at base size and
 * blur on the way up.
 */
.verification-markers {
  position: absolute;
  inset: 0;
  z-index: 4;
  overflow: hidden;
  pointer-events: none;
  transition: opacity 0.12s ease;

  /* Overridden inline from `markerScale`; the declaration here is the fallback
     and the single place the default is stated. */
  --marker-scale: 1;

  --vm-shape: calc(19.5px * var(--marker-scale));
  --vm-shape-border: calc(4.5px * var(--marker-scale));
  --vm-as-built: calc(18px * var(--marker-scale));
  --vm-as-built-border: calc(3px * var(--marker-scale));
  --vm-slice: calc(15px * var(--marker-scale));
  --vm-radius: calc(3px * var(--marker-scale));
  --vm-ring: calc(1.5px * var(--marker-scale));
  --vm-drop-y: calc(1.5px * var(--marker-scale));
  --vm-drop-blur: calc(7.5px * var(--marker-scale));
  --vm-cross-arm: calc(27px * var(--marker-scale));
  --vm-cross-thick: calc(1.5px * var(--marker-scale));
  --vm-label-x: calc(21px * var(--marker-scale));
  --vm-label-max: calc(270px * var(--marker-scale));
  --vm-label-pad-y: calc(4.5px * var(--marker-scale));
  --vm-label-pad-x: calc(9px * var(--marker-scale));
  --vm-label-font: calc(13.5px * var(--marker-scale));
  --vm-label-nudge: calc(19.5px * var(--marker-scale));
}

.verification-marker {
  position: absolute;
  width: 0;
  height: 0;
  color: var(--verification-marker-color);
  will-change: left, top;

  /* `translate(-50%, -50%)` is a percentage of the shape's OWN box, so the
     shape stays centred on the zero-size anchor at every scale. That is what
     keeps the projected point fixed while the marker grows. */
  &__shape {
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    width: var(--vm-shape);
    height: var(--vm-shape);
    border: var(--vm-shape-border) solid var(--verification-marker-color);
    border-radius: 50%;
    background: rgba(5, 18, 20, 0.78);
    box-shadow:
      0 0 0 var(--vm-ring) rgba(255, 255, 255, 0.94),
      0 var(--vm-drop-y) var(--vm-drop-blur) rgba(0, 0, 0, 0.85);
    transform: translate(-50%, -50%);
  }

  &__label {
    position: absolute;
    left: var(--vm-label-x);
    top: 0;
    max-width: var(--vm-label-max);
    overflow: hidden;
    padding: var(--vm-label-pad-y) var(--vm-label-pad-x);
    border-radius: var(--vm-radius);
    background: rgba(4, 14, 17, 0.86);
    box-shadow: 0 var(--vm-drop-y) var(--vm-drop-blur) rgba(0, 0, 0, 0.5);
    color: #fff;
    font-family: $vf-font-mono;
    font-size: var(--vm-label-font);
    font-weight: 500;
    letter-spacing: 0.06em;
    line-height: 1.25;
    text-overflow: ellipsis;
    text-shadow: 0 1px 1px #000;
    transform: translateY(-50%);
    white-space: nowrap;
  }

  &--as-built &__shape {
    width: var(--vm-as-built);
    height: var(--vm-as-built);
    border: var(--vm-as-built-border) solid #fff;
    border-radius: var(--vm-radius);
    background: var(--verification-marker-color);
    box-shadow:
      0 0 0 var(--vm-ring) rgba(0, 0, 0, 0.88),
      0 var(--vm-drop-y) var(--vm-drop-blur) rgba(0, 0, 0, 0.85);
    transform: translate(-50%, -50%) rotate(45deg);
  }

  // Closely aligned design/as-built points are common. Keep both labels legible
  // without moving the marker shapes away from their measured positions. The
  // nudge scales too, so the pair stays separated by the same visual gap.
  &--design &__label,
  &--preview &__label {
    transform: translateY(-50%) translateY(calc(-1 * var(--vm-label-nudge)));
  }

  &--as-built &__label {
    transform: translateY(-50%) translateY(var(--vm-label-nudge));
  }

  &--preview &__shape {
    border-radius: var(--vm-radius);
    background: rgba(255, 255, 255, 0.12);
  }

  &--slice &__shape,
  &--conflict &__shape {
    width: var(--vm-slice);
    height: var(--vm-slice);
    border: var(--vm-as-built-border) solid #fff;
    background: var(--verification-marker-color);

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      background: #fff;
      box-shadow: 0 0 1px #000;
      transform: translate(-50%, -50%);
    }

    &::before {
      width: var(--vm-cross-arm);
      height: var(--vm-cross-thick);
    }

    &::after {
      width: var(--vm-cross-thick);
      height: var(--vm-cross-arm);
    }
  }

  &--conflict &__shape {
    border-style: dashed;
  }

  &--active &__label {
    font-weight: 700;
  }
}

@media (prefers-reduced-motion: reduce) {
  .verification-markers {
    transition: none;
  }
}
</style>
